import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bookingApi } from '../../../utils/bookingApi';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDate } from '../../../utils/dates';
import { PageHeader, FilterChips, Sheet, Skeleton, ErrorState, useConfirm, useToast } from '../../../components/ui';
import BgLapseReports from './BgLapseReports';
import '../booking-shared.css';
import './AdminBgNumbers.css';
import { archiveCreditNote } from '../../../utils/archiveCredit';

// Green all good, amber waiting on the family, red needs staff, grey not in play
const STATE_META = {
  PENDING:              { label: 'Pending',           tone: 'bad',  actionRequired: true },
  INVALID:              { label: 'Invalid',           tone: 'bad',  actionRequired: false },
  EXPIRED_IN_GRACE:     { label: 'Expired (grace)',   tone: 'wait', actionRequired: false },
  EXPIRED_PAST_GRACE:   { label: 'Expired',           tone: 'bad',  actionRequired: true },
  NOT_ON_BG_IN_GRACE:   { label: 'Not on BG (grace)', tone: 'wait', actionRequired: false },
  NOT_ON_BG_PAST_GRACE: { label: 'Not on BG',         tone: 'bad',  actionRequired: true },
  MISSING:              { label: 'No number',         tone: 'off',  actionRequired: false },
  VERIFIED:             { label: 'Verified',          tone: 'good', actionRequired: false },
};

const FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'action', label: 'Action required' },
  { id: 'fyi', label: 'Just FYI' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'archived', label: 'Archived' },
];

// Which filter a row belongs to. Archived and dismissed gymnasts sit on
// their own, out of All, Action required and Just FYI. Dismissed is for
// people who still come now and then: off the to-do list without archiving.
const groupOf = (r) => (r.isArchived || r.familyArchived ? 'archived'
  : r.bgNumberDismissedAt ? 'dismissed'
    : STATE_META[r.bgRowState]?.actionRequired ? 'action' : 'fyi');

// How many gymnasts are waiting on staff, for the BG numbers tile on Admin.
export const bgActionCount = (rows) => rows.filter(r => groupOf(r) === 'action').length;

const NotSet = () => <span className="bk-muted">Not set</span>;

// What staff can do with a row, most used first. Each keeps its own access check.
function rowActions(g, { managesMembers, isClubAdmin }) {
  const live = !g.isArchived && !g.familyArchived && !g.bgNumberDismissedAt;
  const list = [];
  const add = (id, label, help, className = 'bk-btn') => list.push({ id, label, help, className });
  if (g.isArchived) {
    if (isClubAdmin) add('restore', 'Restore');
    return list;
  }
  if (g.familyArchived) return list;
  if (g.bgNumberDismissedAt) {
    if (managesMembers) add('bring-back', 'Bring back');
    if (isClubAdmin) add('archive', 'Archive', null, 'bk-btn bk-btn--outline-danger');
    return list;
  }
  if (managesMembers && g.bgRowState === 'PENDING') {
    add('verify', 'Verify', null, 'bk-btn bk-btn--primary');
    add('expire-pending', 'Valid but expired', 'A real BG number, but the membership has expired.');
    add('not-on-bg', 'Not on BG', "The number looks right but the club isn't added on the BG portal, so we can't see them.");
    add('invalidate', 'Invalid', 'Not a real BG number.');
  }
  if (managesMembers && (g.bgRowState === 'NOT_ON_BG_IN_GRACE' || g.bgRowState === 'NOT_ON_BG_PAST_GRACE')) {
    add('verify', 'Verify', 'The family has added the club on the BG portal: verify the membership.', 'bk-btn bk-btn--primary');
    add('not-on-bg-renudge', 'Re-send nudge', 'Email the family again asking them to add the club on the BG portal.');
  }
  if (managesMembers && g.bgNumber && ['EXPIRED_IN_GRACE', 'EXPIRED_PAST_GRACE', 'INVALID'].includes(g.bgRowState)) {
    add('verify', 'Mark as fixed', 'The membership is renewed or the number sorted out: mark it verified and lift any cancellations the expiry set.', 'bk-btn bk-btn--primary');
  }
  if (live && STATE_META[g.bgRowState]?.actionRequired) {
    if (managesMembers) add('dismiss', 'Dismiss', 'Off Action required without archiving, for someone who only comes now and then. They come back if the family updates their number.');
    if (isClubAdmin) add('archive', 'Archive', "If they're not coming back. Staff are emailed if they look to be returning.", 'bk-btn bk-btn--outline-danger');
  }
  return list;
}

export default function AdminBgNumbers() {
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);
  const [more, setMore] = useState(null); // the row whose other actions are open
  // Archiving and restoring stay with Club admins (the server asks for one);
  // verifying and bringing back need Manage access to members
  const { isClubAdmin, can } = useAuth();
  const managesMembers = can('members', 'MANAGE');
  const confirm = useConfirm();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(() => (FILTER_OPTIONS.some(o => o.id === searchParams.get('filter')) ? searchParams.get('filter') : 'action'));

  const load = useCallback(() => {
    setLoadError(false);
    bookingApi.getAdminBgNumbers()
      .then(r => setRows(r.data.rows || []))
      .catch((err) => { console.error('Could not load BG numbers', err); setLoadError(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const busy = (gymnastId, on) => setSaving(s => ({ ...s, [gymnastId]: on }));

  const run = async (g, actionId) => {
    setMore(null);
    if (actionId === 'archive') {
      const ok = await confirm({
        title: `Archive ${g.firstName} ${g.lastName}?`,
        message: 'This cancels their memberships, standing slots and future bookings, the same as archiving from Gymnasts. Paid future bookings are credited to the family. Staff are emailed if they look to be coming back, and you can restore them from the Archived list.',
        confirmLabel: 'Archive',
      });
      if (!ok) return;
    }
    busy(g.id, true);
    setError(null);
    try {
      if (actionId === 'archive') {
        const res = await bookingApi.archiveFromBg(g.id);
        toast(`${g.firstName} ${g.lastName} archived.${archiveCreditNote(res?.data?.cascade)}`);
      } else if (actionId === 'restore') {
        await bookingApi.restoreGymnast(g.id);
        toast(`${g.firstName} ${g.lastName} restored`);
      } else if (actionId === 'dismiss' || actionId === 'bring-back') {
        await bookingApi.dismissBgNumber(g.id, actionId === 'dismiss');
      } else {
        await bookingApi.verifyBgNumber(g.id, actionId);
      }
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't make that change. Try again.");
    } finally {
      busy(g.id, false);
    }
  };

  const filtered = useMemo(() => {
    const list = (rows || []).filter(r => (filter === 'all' ? ['action', 'fyi'].includes(groupOf(r)) : groupOf(r) === filter));
    const stateOrder = [
      'PENDING',
      'EXPIRED_PAST_GRACE',
      'NOT_ON_BG_PAST_GRACE',
      'EXPIRED_IN_GRACE',
      'NOT_ON_BG_IN_GRACE',
      'INVALID',
      'MISSING',
    ];
    list.sort((a, b) => {
      const ao = stateOrder.indexOf(a.bgRowState);
      const bo = stateOrder.indexOf(b.bgRowState);
      if (ao !== bo) return ao - bo;
      return (b.daysInState ?? 0) - (a.daysInState ?? 0);
    });
    return list;
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = { all: 0, action: 0, fyi: 0, dismissed: 0, archived: 0 };
    for (const r of rows || []) {
      c[groupOf(r)]++;
      if (['action', 'fyi'].includes(groupOf(r))) c.all++;
    }
    return c;
  }, [rows]);

  const header = <PageHeader back={{ to: '/admin-hub', label: 'Admin' }} title="BG numbers" />;

  if (loadError) {
    return (
      <div className="bk-page bk-page--wide">
        {header}
        <ErrorState title="Couldn't load BG numbers" onRetry={load} />
      </div>
    );
  }
  if (!rows) {
    return (
      <div className="bk-page bk-page--wide">
        {header}
        <Skeleton rows={5} label="Loading BG numbers…" />
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--wide">
      {header}
      {error && <p className="bk-form-error" role="alert">{error}</p>}

      <BgLapseReports onChanged={load} />

      <FilterChips
        label="Show"
        chips={FILTER_OPTIONS.map(o => ({ id: o.id, label: `${o.label} (${counts[o.id]})` }))}
        value={filter}
        onChange={setFilter}
      />

      {filtered.length === 0 ? (
        <p className="bk-muted">No gymnasts match this filter.</p>
      ) : (
        <table className="bk-table bg-table">
          <thead>
            <tr>
              <th>Gymnast</th>
              <th>Adult</th>
              <th>BG number</th>
              <th>Status</th>
              <th className="bg-table__days-heading">Days</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(g => {
              const meta = STATE_META[g.bgRowState] || STATE_META.MISSING;
              const guardian = g.guardians?.[0];
              const inGrace = g.bgRowState === 'EXPIRED_IN_GRACE' || g.bgRowState === 'NOT_ON_BG_IN_GRACE';
              const daysText = inGrace && g.graceDaysLeft != null
                ? `${g.graceDaysLeft}d left`
                : (g.daysInState != null ? `${g.daysInState}d` : null);
              const nudgedDaysAgo = g.bgNumberLastNudgedAt
                ? Math.floor((Date.now() - new Date(g.bgNumberLastNudgedAt).getTime()) / (24 * 60 * 60 * 1000))
                : null;
              const actions = rowActions(g, { managesMembers, isClubAdmin });
              const shown = actions.length > 2 ? actions.slice(0, 1) : actions;
              const note = g.isArchived ? `Archived ${g.archivedAt ? formatDate(g.archivedAt) : ''}`.trim()
                : g.familyArchived ? 'Family archived'
                  : g.bgNumberDismissedAt ? `Dismissed ${formatDate(g.bgNumberDismissedAt)}`
                    : nudgedDaysAgo != null && g.bgRowState.startsWith('NOT_ON_BG') ? (nudgedDaysAgo === 0 ? 'Nudged today' : `Nudged ${nudgedDaysAgo}d ago`)
                      : null;
              return (
                <tr key={g.id} className="bg-row">
                  <td className="bg-row__name">{g.firstName} {g.lastName}</td>
                  <td className="bg-row__adult">
                    {guardian ? `${guardian.firstName} ${guardian.lastName}` : <NotSet />}
                  </td>
                  <td className="bg-row__number">{g.bgNumber || <NotSet />}</td>
                  <td className="bg-row__status">
                    <span className={`bk-pill bk-pill--${meta.tone}`}>{meta.label}</span>
                  </td>
                  <td className="bg-row__days">{daysText || <NotSet />}</td>
                  <td className="bg-row__actions">
                    {note && <span className="bg-row__note">{note}</span>}
                    {actions.length > 0 && (
                      <div className="bk-row-actions">
                        {shown.map(a => (
                          <button key={a.id} type="button" className={`${a.className} bk-btn--sm`} disabled={saving[g.id]} onClick={() => run(g, a.id)}>
                            {a.label}
                          </button>
                        ))}
                        {actions.length > 2 && (
                          <button type="button" className="bk-btn bk-btn--sm" disabled={saving[g.id]} onClick={() => setMore(g)}
                            aria-label={`More for ${g.firstName} ${g.lastName}`}>
                            More
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {more && (
        <Sheet
          title={`${more.firstName} ${more.lastName}`}
          onClose={() => setMore(null)}
          footer={<div className="bk-actions"><button type="button" className="bk-btn" onClick={() => setMore(null)}>Close</button></div>}
        >
          <ul className="bg-more">
            {rowActions(more, { managesMembers, isClubAdmin }).slice(1).map(a => (
              <li key={a.id}>
                <button type="button" className={`${a.className} bk-btn--full`} onClick={() => run(more, a.id)}>{a.label}</button>
                {a.help && <p className="bk-small">{a.help}</p>}
              </li>
            ))}
          </ul>
        </Sheet>
      )}
    </div>
  );
}
